'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useAfterInteraction } from '@/lib/use-after-interaction';

// Import dinamico per evitare SSR (Three.js richiede il browser)
const Model3DViewer = dynamic(() => import('./Model3DViewer'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
    </div>
  ),
});

// Three.js (~170 KiB) e il modello vengono caricati solo su desktop e solo
// dopo la prima interazione (o qualche secondo dopo il load), così non
// rallentano il caricamento iniziale della pagina.
const DEFERRED_3D_TIMEOUT_MS = 6000;

// Parole che si alternano con effetto typewriter
const words = ['semplice', 'economico', 'rapido', 'sicuro'];

function TypewriterText() {
  // Parte già con la prima parola scritta: il testo dell'h1 è l'elemento LCP
  // e non deve "crescere" lettera per lettera durante il caricamento.
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState(words[0]);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[currentWordIndex];
    let delay = isDeleting ? 50 : 100; // Backspace più veloce della digitazione
    let step: () => void;

    if (!isDeleting && currentText === currentWord) {
      // Pausa prima di cancellare
      delay = 2000;
      step = () => setIsDeleting(true);
    } else if (isDeleting && currentText === '') {
      // Passa alla parola successiva
      step = () => {
        setIsDeleting(false);
        setCurrentWordIndex((prev) => (prev + 1) % words.length);
      };
    } else if (isDeleting) {
      // Backspace
      step = () => setCurrentText(currentText.slice(0, -1));
    } else {
      // Digitazione
      step = () => setCurrentText(currentWord.slice(0, currentText.length + 1));
    }

    const timeout = setTimeout(step, delay);
    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentWordIndex]);

  return (
    <span className="text-primary-400">
      {currentText}
      <span className="animate-pulse" aria-hidden="true">|</span>
    </span>
  );
}

const svgPattern = "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")";

export default function Hero() {
  const show3D = useAfterInteraction(DEFERRED_3D_TIMEOUT_MS, '(min-width: 1024px)');

  return (
    <section
      id="home"
      className="relative min-h-[85vh] flex items-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary-500 via-secondary-600 to-secondary-700">
        {/* Pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: svgPattern }}
        />
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl" />
      </div>

      {/* 3D Viewer - Full section layer on right side */}
      {show3D && (
        <div className="absolute inset-0 hidden lg:block z-20" style={{ left: '45%' }}>
          <Model3DViewer />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-20 items-center">
          {/* Text content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-6">
              Realizzare prototipi e produzioni in serie non è mai stato così{' '}
              <span className="inline-block min-w-[180px] sm:min-w-[220px] lg:min-w-[280px]">
                <TypewriterText />
              </span>
            </h1>
            
            <p className="text-lg text-secondary-200 mb-8 max-w-xl mx-auto lg:mx-0 animate-slide-up animate-delay-100">
              Riduci i costi ed il tempo associati alle attività di prototipazione e progettazione. 
              Oppure ordina la tua produzione in serie in pochissimi clic.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-slide-up animate-delay-200">
                <Link 
                  href="/contatti"
                  className="btn-primary text-lg px-8 py-4"
                >
                  Richiedi un preventivo
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              <Link href="#servizi" className="btn-outline border-white/30 text-white hover:bg-white/10 hover:text-white">
                Scopri di più
              </Link>
            </div>

            {/* Security disclaimer */}
            <div className="flex items-center gap-2 justify-center lg:justify-start mt-4 animate-slide-up animate-delay-200">
              <svg 
                className="w-3.5 h-3.5 text-secondary-200" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                />
              </svg>
              <span className="text-xs text-secondary-200">
                I tuoi upload sono sicuri e confidenziali
              </span>
            </div>

            {/* Client Logos */}
            <div className="mt-8 pt-6 border-t border-white/10 animate-slide-up animate-delay-300">
              <p className="text-xs text-secondary-200 uppercase tracking-wider mb-4">
                Alcuni dei nostri clienti
              </p>
              <div className="flex items-center gap-6 justify-center lg:justify-start">
                <Image
                  src="/images/csthermos.png"
                  alt="CS Thermos"
                  width={105}
                  height={42}
                  className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity"
                />
                <Image
                  src="/images/weart2.png"
                  alt="Weart"
                  width={105}
                  height={42}
                  className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity"
                />
                <Image
                  src="/images/dororobianco.png"
                  alt="Dororo"
                  width={70}
                  height={28}
                  className="h-7 w-auto opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>

          {/* Spacer for 3D area - maintains grid layout */}
          <div className="hidden lg:block" aria-hidden="true" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
        <svg
          className="w-6 h-6 text-white/50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </div>
    </section>
  );
}
